import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

/**
 * resourcePersonUploadStore — media upload slice (Cloudinary via backend).
 *
 *   POST /api/uploads/image?folder=…      — public image, raw binary body with
 *                                           Content-Type: image/* + x-filename
 *   POST /api/uploads/document            — private doc (≤50MB), raw binary
 *                                           body with file Content-Type + x-filename
 *   DELETE /api/uploads                   — { publicId, cloudType, deliveryType }
 *
 * NOTE: endpoints live at /api/uploads/* (NOT under /api/resource-person/*)
 * but are used by the lecturer when building course covers and lessons.
 * Videos are NOT uploaded — the lecturer pastes a YouTube link into videoUrl.
 */

export const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const DOCUMENT_MAX_BYTES = 50 * 1024 * 1024;

// Human-readable limits for UI hints (KB)
export const IMAGE_MAX_KB = Math.round(IMAGE_MAX_BYTES / 1024);
export const DOCUMENT_MAX_KB = Math.round(DOCUMENT_MAX_BYTES / 1024);

export const resourcePersonUploadInitialState = {
  imageUploadLoading: false,
  imageUploadError: null,
  lastImageUpload: null,
  documentUploadLoading: false,
  documentUploadError: null,
  lastDocumentUpload: null,
};

async function uploadBinary(path, file, filename) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const token = authSession.getToken();
  const headers = {};
  if (file?.type) headers["Content-Type"] = file.type;
  if (filename) headers["x-filename"] = filename;
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await safeFetch(`${API_URL.replace(/\/$/, "")}${path}`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: file,
  });
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      const error = new Error(
        response.status === 413
          ? explainApiError(response.status, {}, "Upload failed.")
          : text.trim().startsWith("<")
            ? `Upload API returned HTML (${response.status}).`
            : `Upload API returned a non-JSON response (${response.status}).`
      );
      error.status = response.status;
      throw error;
    }
  }
  if (!response.ok) {
    const err = new Error(explainApiError(response.status, data || {}, `Upload failed with status ${response.status}.`));
    err.status = response.status;
    err.payload = data;
    throw err;
  }
  return data?.data && typeof data.data === "object" && !Array.isArray(data.data) ? data.data : data;
}

export function createResourcePersonUploadActions(set, get) {
  return {
    // POST /api/uploads/image?folder=… — file: image/* (≤10MB)
    uploadImage: async (file, folder) => {
      set({ imageUploadLoading: true, imageUploadError: null });
      try {
        if (!file) throw new Error("No file selected.");
        if (file.size > IMAGE_MAX_BYTES) {
          throw new Error("Image is too large. Maximum size is 10MB.");
        }
        const path = folder ? `/api/uploads/image?folder=${encodeURIComponent(folder)}` : "/api/uploads/image";
        const result = await uploadBinary(path, file, file.name);
        set({ imageUploadLoading: false, lastImageUpload: result });
        return { success: true, upload: result };
      } catch (error) {
        const message = error.isNetworkError
          ? "The upload did not return a readable response. Check your connection; if it persists, try a smaller cover because the upload service may enforce a lower limit than 10MB."
          : error.message;
        set({ imageUploadLoading: false, imageUploadError: message });
        return { success: false, error: message, status: error.status };
      }
    },

    // POST /api/uploads/document — file: any (≤50MB)
    uploadDocument: async (file) => {
      set({ documentUploadLoading: true, documentUploadError: null });
      try {
        if (!file) throw new Error("No file selected.");
        if (file.size > DOCUMENT_MAX_BYTES) {
          throw new Error("Document is too large. Maximum size is 50MB.");
        }
        const result = await uploadBinary("/api/uploads/document", file, file.name);
        set({ documentUploadLoading: false, lastDocumentUpload: result });
        return { success: true, upload: result };
      } catch (error) {
        set({ documentUploadLoading: false, documentUploadError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    // DELETE /api/uploads { publicId, cloudType, deliveryType }
    deleteUpload: async ({ publicId, cloudType, deliveryType } = {}) => {
      try {
        if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
        const token = authSession.getToken();
        const headers = { Accept: "application/json", "Content-Type": "application/json" };
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await safeFetch(`${API_URL.replace(/\/$/, "")}/api/uploads`, {
          method: "DELETE",
          headers,
          credentials: "omit",
          body: JSON.stringify({ publicId, cloudType, deliveryType }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(explainApiError(response.status, data || {}, `Delete failed with status ${response.status}.`));
        }
        return { success: true, data: data?.data ?? data };
      } catch (error) {
        return { success: false, error: error.message, status: error.status };
      }
    },
  };
}

const resourcePersonUploadActions = createResourcePersonUploadActions;
export default resourcePersonUploadActions;
